import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faAngleLeft,
  faAngleRight,
  faChevronDown,
  faSignOutAlt,
  faLayerGroup,
  faUserShield,
  faXmark,
  faCircleCheck,
  faTriangleExclamation,
  faHourglassHalf,
  faClockRotateLeft,
  faWandMagicSparkles,
  faUsers,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import LoadingBar from 'react-top-loading-bar';
import '@tabler/icons-webfont/dist/tabler-icons.css';
import './Sidebar.scss';
import { eventBus } from '../bus/eventBus';
import { useAuth } from '../context/AuthProvider';
import { isAssistantEnabled } from './Assistant/assistantSettings';
import { useTranslation } from 'react-i18next';
import PatientImage from './Patient/PatientImage';
import AccountModal from './Modal/AccountModal';
import LoginHistoryModal from './Modal/LoginHistoryModal';
import SwitchAccountModal from './Modal/SwitchAccountModal';
import ConfirmModal from './Modal/ConfirmModal';import ContactModal from './Modal/ContactModal';
import CommandPalette from './CommandPalette/CommandPalette';
import { API_BASE_URL } from '../api';

// Main nav, grouped by how often a pharmacist needs each page: the everyday pages as plain
// links (plus Ventes), then collapsible groups for the rest, Paramètres and Support last.
// Administration lives in the user menu at the bottom (footerMenuItems) — fewer rows to scan here.
const menuItems = (t) => [
  {
    label: t('Dashboard'),
    icon: 'ti-layout-dashboard',
    to: '/dashboard',
    requiredRight: 'DASHBOARD_VIEW',
  },
  {
    label: t('See All Products'),
    icon: 'ti-vaccine-bottle',
    to: '/products',
    requiredRight: 'PRODUCTS_VIEW',
  },
  {
    label: t('Sales'),
    icon: 'ti-cash-register',
    key: 'orders',
    children: [
      { label: t('See All orders'), icon: 'ti-list', to: '/orders', alsoActiveOn: ['/order/'], requiredRight: 'ORDERS_VIEW' },
      { label: t('Invoices'), icon: 'ti-file-invoice', to: '/invoices', requiredRight: 'INVOICES_VIEW' },
      { label: t('Payments'), icon: 'ti-credit-card', to: '/payments', requiredRight: 'PAYMENTS_VIEW' },
      { label: t('Returns'), icon: 'ti-arrow-back-up', to: '/returns', requiredRight: 'RETURNS_VIEW' },
    ],
  },
  {
    label: t('Patients'),
    icon: 'ti-users',
    to: '/patients',
    // a patient's own page lives under /patient/:id (singular)
    alsoActiveOn: ['/patient/'],
    requiredRight: 'PATIENTS_VIEW',
  },
  {
    label: t('Calendar'),
    icon: 'ti-calendar',
    to: '/appointements',
    requiredRight: 'CALENDAR_VIEW',
  },
  {
    label: t('Medical Services'),
    icon: 'ti-stethoscope',
    to: '/services',
    requiredRight: 'SERVICES_VIEW',
  },
  {
    label: t('sidebar.group.stock'),
    icon: 'ti-building-warehouse',
    key: 'stock',
    children: [
      { label: t('Inventory'), icon: 'ti-clipboard-list', to: '/inventaire', requiredRight: 'INVENTORY_VIEW' },
      { label: t('Suppliers'), icon: 'ti-truck-delivery', to: '/suppliers', requiredRight: 'SUPPLIERS_VIEW' },
      { label: t('Confrères'), icon: 'ti-building-store', to: '/confreres', requiredRight: 'CONFRERES_VIEW' },
    ],
  },
  {
    label: t('sidebar.group.purchases'),
    icon: 'ti-box-multiple',
    key: 'bons',
    children: [
      { label: t('Purchase Orders'), icon: 'ti-file-text', to: '/purchase-order-imports', requiredRight: 'BONS_VIEW' },
    ],
  },
  {
    label: t('sidebar.group.management'),
    icon: 'ti-briefcase',
    key: 'management',
    children: [
      { label: t('Assurances'), icon: 'ti-shield-heart', to: '/insurances', requiredRight: 'INSURANCE_VIEW' },
      { label: t('Reports'), icon: 'ti-report-analytics', to: '/reports', requiredRight: 'REPORTS_VIEW' },
      { label: t('sidebar.marketing_campaigns'), icon: 'ti-send', to: '/marketing/campaigns', requiredRight: 'MARKETING_VIEW' },
      { label: t('sidebar.marketing_templates'), icon: 'ti-layout-grid', to: '/marketing/templates', requiredRight: 'MARKETING_VIEW' },
    ],
  },
  {
    label: t('Settings'),
    icon: 'ti-settings',
    key: 'settings',
    to: '/settings',
    secondary: true,
    requiredRight: 'SETTINGS_VIEW',
  },
  {
    label: t('Support'),
    icon: 'ti-headset',
    key: 'help',
    secondary: true,
    children: [
      { label: t('Help Center'), icon: 'ti-help', to: '/help' },
      { label: t('Contact Us'), icon: 'ti-mail', key: 'contact' },
    ],
  },
];

// Occasional pages, in the user menu at the bottom of the sidebar (still in the Ctrl+K palette)
const footerMenuItems = (t) => [
  { label: t('Users'), icon: 'ti-users', to: '/admin/users', requiredRight: 'ADMIN_USERS_VIEW' },
];

const Icon = ({ icon, className = '' }) => (
  <i className={`ti ${icon} sidebar-icon ${className}`} />
);

// Ctrl+K on Windows/Linux, ⌘K on macOS
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const formatCount = (n) => (n > 999 ? '999+' : n);

const Sidebar = () => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRight, settings } = useAuth();
  const logoutBarRef = useRef(null);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [activeParentId, setActiveParentId] = useState(null);
  const [popover, setPopover] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [patientCount, setPatientCount] = useState(null);
  const [pendingInventaireCount, setPendingInventaireCount] = useState(null);
  const [ordersTodayCount, setOrdersTodayCount] = useState(null);
  const [accountModalTab, setAccountModalTab] = useState(null);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [showSwitchAccount, setShowSwitchAccount] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const confirmLogout = async () => {
    setLoggingOut(true);
    logoutBarRef.current?.continuousStart();
    try {
      await logout();
    } finally {
      logoutBarRef.current?.complete();
      setLoggingOut(false);
      setShowLogoutConfirm(false);
    }
    navigate('/login');
  };
  const [showContactModal, setShowContactModal] = useState(false);
  const [appointmentsTodayCount, setAppointmentsTodayCount] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Ctrl/Cmd+K anywhere in the app opens the command palette
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
  const sidebarRef = useRef(null);
  const popoverRef = useRef(null);

  // Permission-gated nav — a parent with children (Ventes, Stock, Achats, Gestion, Support) is
  // dropped once none of its children are reachable, rather than left as a dead-end
  // heading; a parent gated directly (Dashboard, Patients, ...) is dropped outright.
  const items = menuItems(t)
    .map(item => item.children
      ? { ...item, children: item.children.filter(child => !child.requiredRight || hasRight(child.requiredRight)) }
      : item)
    .filter(item => !item.requiredRight || hasRight(item.requiredRight))
    .filter(item => !item.children || item.children.length > 0);
  const footerItems = footerMenuItems(t).filter(item => !item.requiredRight || hasRight(item.requiredRight));
  // The desktop icon-only mode is meaningless inside the mobile drawer (opened full-width
  // on purpose) — every render decision that used to key off `collapsed` alone now keys off
  // this instead, while the raw `collapsed` state itself stays untouched so it's restored
  // as-is once the drawer closes back onto a desktop viewport.
  const iconOnly = collapsed && !mobileOpen;

  // Count shown at the end of a row (or as a dot on the icon when collapsed). Tone carries
  // meaning: gray = plain total, blue = today's activity, amber = something waiting.
  const countFor = (item) => {
    if (item.key === 'orders') return { value: ordersTodayCount, tone: 'blue', title: "Ventes aujourd'hui" };
    if (item.key === 'stock') return { value: pendingInventaireCount, tone: 'amber', title: 'Inventaires en attente' };
    if (item.to === '/patients') return { value: patientCount, tone: 'gray', title: 'Patients' };
    if (item.to === '/inventaire') return { value: pendingInventaireCount, tone: 'amber', title: 'Inventaires en attente' };
    if (item.to === '/appointements') return { value: appointmentsTodayCount, tone: 'blue', title: "Rendez-vous aujourd'hui" };
    return null;
  };

  // NavLink only matches its own `to` prefix; detail pages on another path (e.g. /patient/22
  // for "Patients") list theirs in `alsoActiveOn` to keep their menu entry highlighted.
  const linkClass = (base, entry) => ({ isActive }) => {
    const also = entry.alsoActiveOn?.some((prefix) => location.pathname.startsWith(prefix));
    return `${base}${isActive || also ? ' active' : ''}`;
  };

  const renderCount = (item) => {
    const count = countFor(item);
    if (!count || !(count.value > 0)) return null;
    return iconOnly
      ? <span className={`sidebar-icon-badge sidebar-icon-badge--${count.tone}`}>{formatCount(count.value)}</span>
      : (
        <span className={`sidebar-count sidebar-count--${count.tone}`} title={count.title}>
          {formatCount(count.value)}
        </span>
      );
  };

  // Every reachable page, for the command palette (sub-pages labelled "Parent › Child")
  const palettePages = items.flatMap((item) => (item.children
    ? item.children.filter((c) => c.to).map((c) => ({ label: `${item.label} › ${c.label}`, icon: c.icon, to: c.to }))
    : [{ label: item.label, icon: item.icon, to: item.to }]))
    .concat(footerItems.filter((f) => f.to).map((f) => ({ label: f.label, icon: f.icon, to: f.to })));

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/v1/getPatients`, {
      withCredentials: true,
      params: { page: 0, size: 1 },
    })
      .then(({ data }) => setPatientCount(data.totalElements ?? 0))
      .catch(() => setPatientCount(null));

    axios.get(`${API_BASE_URL}/api/v1/inventaire/pending-count`, { withCredentials: true })
      .then(({ data }) => setPendingInventaireCount(data ?? 0))
      .catch(() => setPendingInventaireCount(null));

    axios.get(`${API_BASE_URL}/api/v1/orders/today-count`, { withCredentials: true })
      .then(({ data }) => setOrdersTodayCount(data ?? 0))
      .catch(() => setOrdersTodayCount(null));

    axios.get(`${API_BASE_URL}/api/v1/appointments/today-count`, { withCredentials: true })
      .then(({ data }) => setAppointmentsTodayCount(data ?? 0))
      .catch(() => setAppointmentsTodayCount(null));
  }, []);

  // Close the collapsed-mode popover when the sidebar is expanded again.
  useEffect(() => {
    setPopover(null);
    setTooltip(null);
  }, [collapsed]);

  // Off-canvas drawer on narrow viewports — opened by the hamburger button the
  // layout header renders next to the back button (see MainLayout.js), signaled
  // over the event bus since the header lives outside this component's DOM tree.
  useEffect(() => {
    const handler = () => setMobileOpen((v) => !v);
    eventBus.on('toggleMobileSidebar', handler);
    return () => eventBus.off('toggleMobileSidebar', handler);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close fullscreen with Escape key.
  useEffect(() => {
    if (!fullscreen) return;
    const handleEsc = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [fullscreen]);

  const showTooltip = (e, label) => {
    if (!iconOnly) return;
    const iconRect = e.currentTarget.getBoundingClientRect();
    const sidebarRect = sidebarRef.current.getBoundingClientRect();
    setTooltip({
      label,
      top: iconRect.top + iconRect.height / 2,
      left: sidebarRect.right + 8,
    });
  };

  const hideTooltip = () => setTooltip(null);

  // Close the popover when clicking outside of it.
  useEffect(() => {
    if (!popover) return;

    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopover(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popover]);

  // Fold the open group when clicking outside the sidebar too (e.g. in the page content).
  const hasOpenSubmenu = Object.values(openSubmenus).some(Boolean);
  useEffect(() => {
    if (!hasOpenSubmenu) return;
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setOpenSubmenus({});
        setActiveParentId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hasOpenSubmenu]);

  const toggleSubmenu = (key) => {
    setOpenSubmenus((prev) => {
      const isOpen = prev[key];
      if (isOpen) {
        setActiveParentId(null);
        return {};
      }
      setActiveParentId(key);
      return { [key]: true };
    });
  };

  // Clicking anywhere else in the sidebar (a plain link, or empty space) folds the open group;
  // its own sub-links don't, so the group stays visible while browsing its pages.
  const closeSubmenus = () => {
    setOpenSubmenus({});
    setActiveParentId(null);
  };

  const handleParentClick = (e, item) => {
    setTooltip(null);
    if (iconOnly) {
      const iconRect = e.currentTarget.getBoundingClientRect();
      const sidebarRect = sidebarRef.current.getBoundingClientRect();

      setPopover((prev) => (
        prev && prev.key === item.key
          ? null
          : {
            key: item.key,
            label: item.label,
            children: item.children,
            top: iconRect.top + iconRect.height / 2,
            left: sidebarRect.right + 8,
          }
      ));
    } else if (item.children.length === 1) {
      // A single-item group has no real choice to make — go straight to it,
      // keeping the submenu open rather than making the user click twice.
      setOpenSubmenus({ [item.key]: true });
      setActiveParentId(item.key);
      navigate(item.children[0].to);
    } else {
      toggleSubmenu(item.key);
    }
  };

  return (
    <>
      <LoadingBar color="#007bff" ref={logoutBarRef} height={3} shadow={true} />
      {mobileOpen && <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />}
      <div ref={sidebarRef} className={`sidebar ${iconOnly ? 'collapsed' : ''} ${fullscreen ? 'fullscreen' : ''} ${mobileOpen ? 'mobile-open' : ''} no-select`}>

      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-logo-wrap" onClick={() => setFullscreen(!fullscreen)}>
          <img src={`${process.env.PUBLIC_URL}/images/logo_primary.png`} alt="SAYDALANI Logo" style={{height: '30px'}} loading="lazy" />
        </div>
        {fullscreen ? (
          <button className="collapse-button" onClick={() => setFullscreen(false)}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        ) : (
          <>
            <button className="collapse-button sidebar-mobile-close" onClick={() => setMobileOpen(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <button className="collapse-button sidebar-desktop-collapse" onClick={() => setCollapsed(!collapsed)}>
              <FontAwesomeIcon icon={collapsed ? faAngleRight : faAngleLeft} />
            </button>
          </>
        )}
      </div>

      {/* Command palette trigger */}
      {!fullscreen && (
        <div className="sidebar-actions">
          <button
            type="button"
            className="sidebar-search"
            onClick={() => { setTooltip(null); setPaletteOpen(true); }}
            onMouseEnter={(e) => showTooltip(e, `${t('sidebar.search')} (${IS_MAC ? '⌘' : 'Ctrl'}+K)`)}
            onMouseLeave={hideTooltip}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            {!iconOnly && (
              <>
                <span>{t('sidebar.search')}</span>
                <kbd>{IS_MAC ? '⌘' : 'Ctrl'} K</kbd>
              </>
            )}
          </button>
        </div>
      )}

      {fullscreen ? (
        /* ── Desktop / App Launcher Mode ── */
        <div className="fullscreen-nav">
          <div className="fullscreen-nav__primary">
            {items.map(item => (
              <NavLink
                key={item.to || item.key}
                to={item.to || item.children?.[0]?.to || '/'}
                className="fullscreen-card"
                onClick={() => setFullscreen(false)}
              >
                <i className={`ti ${item.icon}`} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
          <p className="fullscreen-nav__hint">Appuyez sur Échap pour fermer</p>
        </div>
      ) : (
        <>
          {/* Nav */}
          <div className="sidebar-nav-wrapper" onClick={(e) => { if (e.target === e.currentTarget) closeSubmenus(); }}>
            <nav className="sidebar-nav" onClick={(e) => { if (e.target === e.currentTarget) closeSubmenus(); }}>
              {items.map((item) => {
                const isParent = !!item.children;

                /* ── Parent with submenu ── */
                if (isParent) {
                  // the current page belongs to this group (shown on the parent row while folded)
                  const hasActiveChild = item.children.some((child) => child.to && (
                    location.pathname.startsWith(child.to)
                    || child.alsoActiveOn?.some((prefix) => location.pathname.startsWith(prefix))
                  ));
                  return (
                    <div key={item.key} className="sidebar-link-wrapper">
                      <div
                        className={`sidebar-link with-sub${hasActiveChild && !openSubmenus[item.key] ? ' has-active-child' : ''}`}
                        onClick={(e) => handleParentClick(e, item)}
                        onMouseEnter={(e) => showTooltip(e, item.label)}
                        onMouseLeave={hideTooltip}
                      >
                        <span className="sidebar-icon-wrap">
                          <Icon icon={item.icon} className={item.secondary ? 'sidebar-icon--secondary' : ''} />
                          {iconOnly && renderCount(item)}
                        </span>
                        {!iconOnly && (
                          <>
                            <span>{item.label}</span>
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className={`submenu-chevron ${openSubmenus[item.key] ? 'open' : ''}`}
                            />
                            {/* after the chevron, at the row's end — lined up with the plain links' counts */}
                            {renderCount(item)}
                          </>
                        )}
                      </div>

                      <div className={`submenu ${openSubmenus[item.key] ? 'open' : ''}`}>
                        {item.children.map((child) => (
                          child.key === 'contact' ? (
                            <button
                              key={child.key}
                              type="button"
                              className="sidebar-sublink"
                              onClick={() => setShowContactModal(true)}
                            >
                              {child.label}
                            </button>
                          ) : (
                            <NavLink key={child.to} to={child.to} className={linkClass('sidebar-sublink', child)}>
                              <span className="sidebar-sublink__label">{child.label}</span>
                              {renderCount(child)}
                            </NavLink>
                          )
                        ))}
                      </div>
                    </div>
                  );
                }

                /* ── Simple link ── */
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={linkClass('sidebar-link', item)}
                    onClick={closeSubmenus}
                    onMouseEnter={(e) => showTooltip(e, item.label)}
                    onMouseLeave={hideTooltip}
                  >
                    <span className="sidebar-icon-wrap">
                      <Icon icon={item.icon} className={item.secondary ? 'sidebar-icon--secondary' : ''} />
                      {iconOnly && renderCount(item)}
                    </span>
                    {!iconOnly && <span>{item.label}</span>}
                    {!iconOnly && renderCount(item)}
                  </NavLink>
                );
              })}
            </nav>
          </div>

          {/* User section */}
          <div className="sidebar-footer">
            <div
              className="user-info d-flex align-items-center gap-2 dropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
            >
              <PatientImage patient={user} />

              {!iconOnly && (
                <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="user-details">
                    <p className="user-name mb-1">
                      {user.firstName} {user.lastName[0]}.
                      {user.subscriptionStatus === 'ACTIVE' && (
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="user-verified-badge"
                          data-tooltip-id="tooltip"
                          data-tooltip-content="Pharmacie à jour de ses factures"
                        />
                      )}
                      {user.subscriptionStatus === 'EXPIRED' && (
                        <FontAwesomeIcon
                          icon={faTriangleExclamation}
                          className="user-verified-badge user-verified-badge--warn"
                          data-tooltip-id="tooltip"
                          data-tooltip-content="Pharmacie non à jour de ses factures"
                        />
                      )}
                      {user.subscriptionStatus === 'TRIAL' && (
                        <FontAwesomeIcon
                          icon={faHourglassHalf}
                          className="user-verified-badge user-verified-badge--trial"
                          data-tooltip-id="tooltip"
                          data-tooltip-content="Période d'essai en cours"
                        />
                      )}
                    </p>
                    <small className="user-pharmacy">{user.pharmacy.name}</small>
                    {user.roleNames?.length > 0 && (
                      <small
                        className="user-role"
                        data-tooltip-id="tooltip"
                        data-tooltip-content={user.roleNames.join(', ')}
                      >
                        {user.roleNames[0]}
                        {user.roleNames.length > 1 && ` +${user.roleNames.length - 1}`}
                      </small>
                    )}
                  </div>
                  <FontAwesomeIcon icon={faChevronDown} className="fa-caret" style={{ marginInlineStart: 'auto' }} />
                </div>
              )}
            </div>

            <ul className="dropdown-menu sb-menu">
              <li>
                <button type="button" className="dropdown-item" onClick={() => setAccountModalTab('accountInformation')}>
                  <span className="sb-menu__icon"><FontAwesomeIcon icon={faUser} /></span> {t('Account')}
                </button>
              </li>
              <li>
                <button type="button" className="dropdown-item" onClick={() => setAccountModalTab('subscriptionPlan')}>
                  <span className="sb-menu__icon"><FontAwesomeIcon icon={faLayerGroup} /></span> {t('Subscription Plan')}
                </button>
              </li>
              <li><hr className="dropdown-divider" /></li>
              {/* occasional pages moved out of the main nav (footerMenuItems) */}
              {footerItems.map((f) => (
                <li key={f.to}>
                  <NavLink to={f.to} className="dropdown-item">
                    <span className="sb-menu__icon"><i className={`ti ${f.icon}`} /></span> {f.label}
                  </NavLink>
                </li>
              ))}
              <li>
                <NavLink to="/admin/roles" className="dropdown-item">
                  <span className="sb-menu__icon"><FontAwesomeIcon icon={faUserShield} /></span> {t('Roles & Permissions')}
                </NavLink>
              </li>
              <li>
                <button type="button" className="dropdown-item" onClick={() => setShowLoginHistory(true)}>
                  <span className="sb-menu__icon"><FontAwesomeIcon icon={faClockRotateLeft} /></span> {t('Login History')}
                </button>
              </li>
              {isAssistantEnabled(settings) && (
                <li>
                  <button type="button" className="dropdown-item" onClick={() => eventBus.emit('openAssistant')}>
                    <span className="sb-menu__icon"><FontAwesomeIcon icon={faWandMagicSparkles} /></span> Assistant Saydalani
                  </button>
                </li>
              )}
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button type="button" className="dropdown-item" onClick={() => setShowSwitchAccount(true)}>
                  <span className="sb-menu__icon"><FontAwesomeIcon icon={faUsers} /></span> Changer de compte
                </button>
              </li>
              <li>
                <a
                  href="#logout"
                  className="dropdown-item sb-menu__item--danger"
                 
                  onClick={(e) => { e.preventDefault(); setShowLogoutConfirm(true); }}
                >
                  <span className="sb-menu__icon"><FontAwesomeIcon icon={faSignOutAlt} /></span>
                  {t('Logout')}
                </a>
              </li>
            </ul>
          </div>
        </>
      )}

      {tooltip && !popover && createPortal(
        <div
          className="sidebar-tooltip"
          style={{ top: tooltip.top, left: tooltip.left }}
        >
          {tooltip.label}
        </div>,
        document.body
      )}

      {popover && createPortal(
        <div
          ref={popoverRef}
          className="sidebar-popover"
          style={{ top: popover.top, left: popover.left }}
        >
          <div className="sidebar-popover-label">{popover.label}</div>
          {popover.children.map((child) => (
            child.key === 'contact' ? (
              <button
                key={child.key}
                type="button"
                className="sidebar-popover-item"
                onClick={() => { setPopover(null); setShowContactModal(true); }}
              >
                {child.label}
              </button>
            ) : (
              <NavLink
                key={child.to}
                to={child.to}
                className="sidebar-popover-item"
                onClick={() => setPopover(null)}
              >
                {child.label}
              </NavLink>
            )
          ))}
        </div>,
        document.body
      )}

      <AccountModal
        show={!!accountModalTab}
        initialTab={accountModalTab || 'accountInformation'}
        onClose={() => setAccountModalTab(null)}
      />

      <LoginHistoryModal
        show={showLoginHistory}
        onClose={() => setShowLoginHistory(false)}
      />

      <SwitchAccountModal
        show={showSwitchAccount}
        onClose={() => setShowSwitchAccount(false)}
      />

      <ConfirmModal
        id="logoutConfirmModal"
        show={showLogoutConfirm}
        title="Se déconnecter ?"
        description="Vous devrez saisir à nouveau vos identifiants pour revenir."
        confirmLabel={t('Logout')}
        confirmVariant="danger"
        icon={faSignOutAlt}
        isLoading={loggingOut}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={confirmLogout}
      />
      <ContactModal
        show={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
    </div>
    <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} pages={palettePages} />
    </>
  );
};

export default Sidebar;
